import { useMemo } from 'react';

import { useAccount } from 'wagmi';

import { I_Winner } from '@/app/[locale]/(main)/profile/_components/ProfilePredictionDialog/WinnerFlashCard';

interface I_UseUserWinStatusProps {
  transformWinners: {
    prime: I_Winner[];
    flash: I_Winner[];
  };
  activeTab: 'prime' | 'flash';
  primeData: { items: unknown[] } | null;
  flashData: { items: unknown[] } | null;
  isLoadingPrime: boolean;
  isLoadingFlash: boolean;
  selectedParticipation?: {
    hasClaimed?: boolean;
  } | null;
}

export const useUserWinStatus = ({
  transformWinners,
  activeTab,
  primeData,
  flashData,
  isLoadingPrime,
  isLoadingFlash,
  selectedParticipation
}: I_UseUserWinStatusProps) => {
  const { address: connectedAddress } = useAccount();

  const userHasWonPrime = useMemo(() => {
    if (!connectedAddress || !transformWinners.prime.length) return false;
    return transformWinners.prime.some(
      w => w.userAddress?.toLowerCase() === connectedAddress?.toLowerCase()
    );
  }, [connectedAddress, transformWinners.prime]);

  const userHasWonFlash = useMemo(() => {
    if (!connectedAddress || !transformWinners.flash.length) return false;
    return transformWinners.flash.some(
      w => w.userAddress?.toLowerCase() === connectedAddress?.toLowerCase()
    );
  }, [connectedAddress, transformWinners.flash]);

  const shouldShowClaimButton = useMemo(() => {
    if (activeTab === 'prime') {
      const hasPrimeData = (primeData?.items.length || 0) > 0;
      const isLoading = isLoadingPrime;
      if (isLoading || !hasPrimeData) return false;
      return userHasWonPrime && !selectedParticipation?.hasClaimed;
    } else {
      const hasFlashData = (flashData?.items.length || 0) > 0;
      const isLoading = isLoadingFlash;
      if (isLoading || !hasFlashData) return false;
      return userHasWonFlash && !selectedParticipation?.hasClaimed;
    }
  }, [
    activeTab,
    primeData,
    flashData,
    isLoadingPrime,
    isLoadingFlash,
    userHasWonPrime,
    userHasWonFlash,
    selectedParticipation?.hasClaimed
  ]);

  const isAlreadyClaimed = selectedParticipation?.hasClaimed || false;

  return {
    userHasWonPrime,
    userHasWonFlash,
    shouldShowClaimButton,
    isAlreadyClaimed
  };
};
